/**
 * Active / Inactive control in the supplier header.
 *
 * Three renderings, chosen by who is looking:
 *  - Valuechain staff / superusers: the CR-4056 company toggle. Deactivating
 *    drops the supplier's codes out of the ERP feed for every customer, so it
 *    stays restricted and audited server-side.
 *  - Customer admins (V9, customer review 11 Sep): the same-looking button, but
 *    it flips the RELATIONSHIP between the admin's own company and this
 *    supplier — the supplier stays active for everyone else. Confirmed in a
 *    dialog because it hides the supplier from the company's lists.
 *  - Everyone else: a read-only badge.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Power } from 'lucide-react';
import { suppliersAPI, SupplierProfile } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { toast } from 'sonner';

interface ActiveStatusToggleProps {
  supplier: SupplierProfile;
  /** Customer admin of the viewing company (see useSupplierDetail). */
  isCustomerAdmin: boolean;
  /** Re-fetch the supplier after a successful change. */
  onChanged: () => Promise<void> | void;
}

const pillClass = (active: boolean, interactive: boolean) =>
  `ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
    interactive ? 'transition-colors disabled:opacity-50 ' : ''
  }${
    active
      ? `bg-green-50 text-green-700 border-green-200${interactive ? ' hover:bg-green-100' : ''}`
      : `bg-red-50 text-red-700 border-red-200${interactive ? ' hover:bg-red-100' : ''}`
  }`;

export function ActiveStatusToggle({ supplier, isCustomerAdmin, onChanged }: ActiveStatusToggleProps) {
  const { user: authUser } = useAuth();
  const isStaff = !!authUser?.is_staff || !!authUser?.is_superuser;
  const [toggling, setToggling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const companyActive = supplier.company_details.is_active;
  // Null/undefined = the API had no single relationship to report on (helix or
  // supplier viewer), so the company flag is the only status there is.
  const relationshipActive = supplier.relationship_is_active ?? null;

  // CR-4056: staff toggle the supplier company itself.
  if (isStaff) {
    const toggleCompany = async () => {
      try {
        setToggling(true);
        const newState = !companyActive;
        await suppliersAPI.toggleSupplierActive(supplier.company, newState);
        toast.success(`Supplier ${newState ? 'activated' : 'deactivated'} successfully`);
        await onChanged();
      } catch (err: unknown) {
        // The API envelope carries `message`, not DRF's `detail`; reading the
        // wrong key hid the permission message behind a generic toast.
        toast.error(getApiErrorMessage(err, 'Failed to update supplier status'));
      } finally {
        setToggling(false);
      }
    };
    return (
      <button
        onClick={toggleCompany}
        disabled={toggling}
        className={pillClass(companyActive, true)}
        title={`Click to ${companyActive ? 'deactivate' : 'activate'} supplier`}
      >
        {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
        {companyActive ? 'Active' : 'Inactive'}
      </button>
    );
  }

  // V9: customer admins toggle their own relationship. Only offered when the
  // API reported one (relationship_is_active is non-null for customer viewers).
  if (isCustomerAdmin && relationshipActive !== null) {
    const nextState = !relationshipActive;
    const confirmToggle = async () => {
      try {
        setToggling(true);
        const res = await suppliersAPI.toggleSupplierRelationshipActive(supplier.company, nextState);
        toast.success(res.data.message || `Supplier ${nextState ? 'activated' : 'deactivated'} for your company`);
        setConfirmOpen(false);
        await onChanged();
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, 'Failed to update supplier status'));
      } finally {
        setToggling(false);
      }
    };
    return (
      <>
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={toggling}
          className={pillClass(relationshipActive, true)}
          title={`Click to ${relationshipActive ? 'deactivate' : 'reactivate'} this supplier for your company`}
        >
          {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Power className="h-3 w-3" />}
          {relationshipActive ? 'Active' : 'Inactive (your company)'}
        </button>
        <Dialog open={confirmOpen} onOpenChange={(open) => { if (!toggling) setConfirmOpen(open); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {nextState ? 'Reactivate' : 'Deactivate'} {supplier.company_name} for your company?
              </DialogTitle>
              <DialogDescription>
                {nextState
                  ? 'This supplier will be marked active for your company again and return to your supplier lists and pickers.'
                  : 'This supplier will be marked inactive for your company only; it stays active for other customers. Open orders are not affected.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={toggling}>
                Cancel
              </Button>
              <Button
                variant={nextState ? 'default' : 'destructive'}
                onClick={confirmToggle}
                disabled={toggling}
              >
                {toggling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Power className="h-4 w-4 mr-2" />}
                {nextState ? 'Reactivate' : 'Deactivate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Read-only. A relationship the viewer's company has switched off outranks
  // the company flag in what it tells this viewer.
  const showRelationshipInactive = relationshipActive === false;
  const active = showRelationshipInactive ? false : companyActive;
  return (
    <span
      className={pillClass(active, false)}
      title={
        showRelationshipInactive
          ? 'Deactivated for your company by a customer admin'
          : 'Only Valuechain staff or a customer admin can activate or deactivate a supplier'
      }
    >
      <Power className="h-3 w-3" />
      {showRelationshipInactive ? 'Inactive (your company)' : companyActive ? 'Active' : 'Inactive'}
    </span>
  );
}
